import type { InventoryListItem, StorefrontPrice } from '@/lib/api/schemas/inventory.schema'
import { MarketModifiers } from '@/components/warehouse/MarketModifiers'
import { SectionCard, ValueOrReason } from '@/components/warehouse/primitives'
import { StorefrontMark } from '@/components/warehouse/storefronts'
import { formatRubleAmount, formatRublePerGram } from '@/lib/market-money'
import { ymarketShowcaseFigure } from '@/lib/warehouse/ymarket-showcase'

const storefrontLabels: Record<StorefrontPrice['target'], string> = {
  wildberries: 'Wildberries',
  ozon: 'Ozon',
  ymarket: 'Яндекс Маркет',
  ori_site: 'ORI',
}

const storefrontOrder: readonly StorefrontPrice['target'][] = ['ymarket', 'wildberries', 'ozon', 'ori_site']

const OURS_WITHOUT_BUYER_PRICE = 'Цена для покупателя не собрана — показана наша цена'

interface PriceFigure {
  amount: number | null
  unavailableReason: string
  perGram: number | null
  perGramUnavailableReason: string
  description: string | undefined
  /** Our own price standing in for an uncollected buyer-side one. */
  ours: boolean
}

function perGramOf(amount: number | null, variant: InventoryListItem): number | null {
  const grams = variant.attributes.netWeightGrams
  return amount !== null && grams !== null && grams > 0 ? amount / grams : null
}

function perGramReason(variant: InventoryListItem): string {
  return variant.attributes.netWeightUnavailableReason ?? 'unit.weight_not_recorded'
}

function onecFigure(variant: InventoryListItem): PriceFigure {
  return {
    amount: variant.onecPrice,
    unavailableReason: variant.onecPriceUnavailableReason ?? 'price.not_provided',
    perGram: perGramOf(variant.onecPrice, variant),
    perGramUnavailableReason: perGramReason(variant),
    description: undefined,
    ours: false,
  }
}

function ymarketFigure(variant: InventoryListItem): PriceFigure {
  const figure = ymarketShowcaseFigure(variant.ymarketEconomics, variant.attributes.netWeightGrams, variant.attributes.netWeightUnavailableReason)
  return { ...figure, ours: false }
}

function marketplaceFigure(price: StorefrontPrice, variant: InventoryListItem): PriceFigure {
  const listed = price.price !== null && price.connectionState !== 'not_connected' && price.listed
  const buyer = listed ? price.estimatedBuyerPrice ?? null : null
  const amount = listed ? buyer ?? price.price : null
  return {
    amount,
    unavailableReason: price.unavailableReason ?? 'price.not_provided',
    perGram: perGramOf(amount, variant),
    perGramUnavailableReason: perGramReason(variant),
    description: amount === null
      ? undefined
      : buyer === null ? OURS_WITHOUT_BUYER_PRICE : `Покупатель заплатит ${formatRubleAmount(buyer)} · наша цена ${formatRubleAmount(price.price)}`,
    ours: amount !== null && buyer === null,
  }
}

/** One storefront: its price large, ₽/г under it, every other detail in the tooltip. */
function StorefrontPriceTile({ storefront, label, figure }: { storefront: string; label: string; figure: PriceFigure }) {
  return (
    <div
      className="min-w-0 rounded-md border border-rule bg-bone/40 px-3 py-3"
      data-testid={`card-price-${storefront}`}
      data-price-basis={figure.amount === null ? undefined : figure.ours ? 'ours' : 'buyer'}
      title={figure.description}
    >
      <dt className="flex items-center gap-1.5 text-xs font-medium text-ink-2">
        {storefront === 'onec' ? null : <StorefrontMark providerCode={storefront} size={14} />}
        <span>{label}</span>
      </dt>
      <dd className="mt-1.5">
        {figure.amount === null ? (
          <span className="block text-2xl leading-tight"><ValueOrReason value={null} reason={figure.unavailableReason} /></span>
        ) : (
          <>
            <span className={`block whitespace-nowrap text-2xl font-semibold leading-tight tabular-nums ${figure.ours ? 'text-ink-muted' : 'text-ink'}`} data-price-role="selling-price">
              {formatRubleAmount(figure.amount)}
            </span>
            <span className="mt-1 block text-sm tabular-nums text-ink-2" data-price-role="per-gram">
              {figure.perGram === null ? <ValueOrReason value={null} reason={figure.perGramUnavailableReason} /> : formatRublePerGram(figure.perGram)}
            </span>
          </>
        )}
      </dd>
    </div>
  )
}

function shown(price: StorefrontPrice): boolean {
  return price.price !== null || (price.connectionState !== 'not_connected' && price.unavailableReason !== null)
}

/** The storefront tiles in a fixed order; Yandex Market whenever its economics exist, since its price is the showcase. */
interface StorefrontTile {
  target: StorefrontPrice['target']
  figure: PriceFigure
}

function storefrontTiles(variant: InventoryListItem): StorefrontTile[] {
  const byTarget = new Map(variant.storefrontPrices.map((price) => [price.target, price]))
  const hasEconomics = variant.ymarketEconomics !== null && variant.ymarketEconomics !== undefined
  return storefrontOrder.flatMap((target): StorefrontTile[] => {
    const price = byTarget.get(target)
    if (target === 'ymarket') {
      return hasEconomics || (price !== undefined && shown(price)) ? [{ target, figure: ymarketFigure(variant) }] : []
    }
    return price !== undefined && shown(price) ? [{ target, figure: marketplaceFigure(price, variant) }] : []
  })
}

export function ProductPrices({ variant }: { variant: InventoryListItem }) {
  const tiles = storefrontTiles(variant)
  const hasOnecPrice = variant.onecPrice !== null || variant.onecPriceUnavailableReason !== null

  return (
    <SectionCard title="Цены">
      {!hasOnecPrice && tiles.length === 0 ? (
        <p className="text-xs text-ink-muted">Цены не зафиксированы.</p>
      ) : (
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {hasOnecPrice ? <StorefrontPriceTile storefront="onec" label="1С" figure={onecFigure(variant)} /> : null}
          {tiles.map(({ target, figure }) => (
            <StorefrontPriceTile key={target} storefront={target} label={storefrontLabels[target]} figure={figure} />
          ))}
        </dl>
      )}
      {variant.ymarketEconomics ? <MarketModifiers economics={variant.ymarketEconomics} weightGrams={variant.attributes.netWeightGrams} /> : null}
    </SectionCard>
  )
}
